import React, { useState, useEffect, useContext } from 'react';
import { FaUpload, FaSpinner, FaCheck, FaTimes } from 'react-icons/fa';
import { ApplicantService } from '../../services/ApplicantService';
import countryPhoneCodes from '../../utils/countryPhoneCodes';
import { AuthContext } from '../../context/AuthContext';
import ErrorPopup from '../ErrorPopup/ErrorPopup';

const ApplicationForm = ({ opportunityId, onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    contact_mobile: '',
    email: '',
    comments: ''
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryInput, setCountryInput] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isOtherCountry, setIsOtherCountry] = useState(false);
  const { userDetails: user } = useContext(AuthContext);
  const [showErrorPopup, setShowErrorPopup] = useState(false);

  useEffect(() => {
    setCountries(countryPhoneCodes.map(c => c.name).sort());
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (!isOtherCountry && formData.country) {
      const found = countryPhoneCodes.find(c => c.name === formData.country);
      setCountryCode(found ? found.code : '');
    }
  }, [formData.country, isOtherCountry]);

  useEffect(() => {
    if (errors.submit || showErrorPopup) {
    }
  }, [errors.submit, showErrorPopup]);

  const handleCountryChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, country: value }));
    setIsOtherCountry(value === 'Other');
    if (value !== 'Other') {
      setCountryInput('');
      const found = countryPhoneCodes.find(c => c.name === value);
      setCountryCode(found ? found.code : '');
    } else {
      setCountryCode('');
    }
    if (errors.country) {
      setErrors(prev => ({ ...prev, country: '' }));
    }
  };

  const handleCountryInput = (e) => {
    setCountryInput(e.target.value);
    setFormData(prev => ({ ...prev, country: e.target.value }));
    if (errors.country) {
      setErrors(prev => ({ ...prev, country: '' }));
    }
  };

  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
    if (errors.countryCode) {
      setErrors(prev => ({ ...prev, countryCode: '' }));
    }
  };

  const handlePhoneNumberChange = (e) => {
    setPhoneNumber(e.target.value);
    if (errors.phoneNumber) {
      setErrors(prev => ({ ...prev, phoneNumber: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.country || (isOtherCountry && !countryInput.trim())) {
      newErrors.country = 'Country is required';
    }

    if (!countryCode.trim()) {
      newErrors.countryCode = 'Country code is required';
    }
    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    if (!resumeFile) {
      newErrors.resume = 'Resume file is required';
    } else {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(resumeFile.type)) {
        newErrors.resume = 'Please upload a PDF, DOC, or DOCX file';
      }
      if (resumeFile.size > 10 * 1024 * 1024) {
        newErrors.resume = 'File size must be less than 10MB';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setResumeFile(file);
    
    // Clear error when user selects a file
    if (errors.resume) {
      setErrors(prev => ({
        ...prev,
        resume: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('opportunity_id', opportunityId);
      formDataToSend.append('name', formData.name);
      formDataToSend.append('country', formData.country);
      formDataToSend.append('contact_mobile', `${countryCode} ${phoneNumber}`.trim());
      formDataToSend.append('email', formData.email);
      formDataToSend.append('resume', resumeFile);
      formDataToSend.append('comments', formData.comments);

      const response = await ApplicantService.uploadResume(formDataToSend);
      
      if (response && response.error) {
        let errorMessage = response.error;
        if (errorMessage.includes('Application already exists for this email')) {
          errorMessage = 'An application has already been submitted for this email address.';
        }
        setErrors({ submit: errorMessage });
        setShowErrorPopup(true);
        if (onError) onError(response);
        setLoading(false);
        return;
      }

      if (response && response.message) {
        setSuccess(true);
        setFormData({
          name: '',
          country: '',
          contact_mobile: '',
          email: '',
          comments: ''
        });
        setResumeFile(null);
        setCountryCode('');
        setPhoneNumber('');
        setCountryInput('');
        setIsOtherCountry(false);
        
        if (onSuccess) {
          onSuccess(response);
        }
      }
    } catch (error) {
      console.error('Application submission failed:', error);
      let errorMessage = error?.response?.data?.error || 'Failed to submit application. Please try again.';
      if (
        error?.response?.status === 409 &&
        errorMessage.includes('Application already exists for this email')
      ) {
        errorMessage = 'An application has already been submitted for this email address.';
      }
      setErrors({ submit: errorMessage });
      setShowErrorPopup(true);
      
      if (onError) {
        onError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaCheck className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-[#1B222A] mb-2">Application Submitted!</h3>
        <p className="text-[#1B222A]/70">
          Thank you for your application. We have received your submission and will review it shortly.
        </p>
      </div>
    );
  }

    return (
    <div className="w-full">
      <ErrorPopup
        message={errors.submit}
        visible={showErrorPopup && !!errors.submit}
        onClose={() => setShowErrorPopup(false)}
      />
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-[#1B222A] mb-2">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md ${
                errors.name ? 'border-red-500' : ''
              }`}
              placeholder="Enter your full name"
              readOnly={!!user}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-[#1B222A] mb-2">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className={`w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md ${
                errors.email ? 'border-red-500' : ''
              }`}
              placeholder="Enter your email address"
              readOnly={!!user}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Country Field */}
          <div>
            <label htmlFor="country" className="block text-sm font-semibold text-[#1B222A] mb-2">
              Country <span className="text-red-500">*</span>
            </label>
            <select
              id="country"
              name="country"
              value={isOtherCountry ? 'Other' : formData.country}
              onChange={handleCountryChange}
              className={`w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md ${errors.country ? 'border-red-500' : ''}`}
            >
              <option value="">Select your country</option>
              {countries.map((country) => (
                <option key={country} value={country}>{country}</option>
              ))}
              <option value="Other">Other (please specify)</option>
            </select>
            {isOtherCountry && (
              <input
                type="text"
                id="countryInput"
                name="countryInput"
                value={countryInput}
                onChange={handleCountryInput}
                className="w-full mt-2 px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md"
                placeholder="Enter your country"
              />
            )}
            {errors.country && (
              <p className="mt-1 text-sm text-red-600">{errors.country}</p>
            )}
          </div>

          {/* Contact Mobile Field (split) */}
          <div>
            <label htmlFor="contact_mobile" className="block text-sm font-semibold text-[#1B222A] mb-2">
              Contact Number <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="countryCode"
                name="countryCode"
                value={countryCode}
                onChange={handleCountryCodeChange}
                className={`w-1/3 px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md ${errors.countryCode ? 'border-red-500' : ''}`}
                placeholder="+XX"
                disabled={!isOtherCountry && !!formData.country}
              />
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                className={`w-2/3 px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md ${errors.phoneNumber ? 'border-red-500' : ''}`}
                placeholder="Enter your contact number"
              />
            </div>
            {errors.countryCode && (
              <p className="mt-1 text-sm text-red-600">{errors.countryCode}</p>
            )}
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-red-600">{errors.phoneNumber}</p>
            )}
          </div>
        </div>

        {/* Resume Upload Field */}
        <div>
          <label htmlFor="resume" className="block text-sm font-semibold text-[#1B222A] mb-2">
            Resume/CV <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="file"
              id="resume"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="hidden"
            />
            <label
              htmlFor="resume"
              className={`w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl cursor-pointer flex items-center justify-center transition-all duration-300 shadow-sm hover:shadow-md ${
                errors.resume ? 'border-red-500 bg-red-50' : 'hover:bg-white focus:border-[#DB4F40]'
              }`}
            >
              <FaUpload className="w-4 h-4 mr-2 text-[#DB4F40]" />
              {resumeFile ? resumeFile.name : 'Choose a file (PDF, DOC, DOCX)'}
            </label>
          </div>
          {errors.resume && (
            <p className="mt-1 text-sm text-red-600">{errors.resume}</p>
          )}
          <p className="mt-1 text-xs text-[#1B222A]/60">
            Maximum file size: 10MB. Supported formats: PDF, DOC, DOCX
          </p>
        </div>

        {/* Comments Field */}
        <div>
          <label htmlFor="comments" className="block text-sm font-semibold text-[#1B222A] mb-2">
            Comments (optional)
          </label>
          <textarea
            id="comments"
            name="comments"
            value={formData.comments}
            onChange={handleInputChange}
            className="w-full px-4 py-3 bg-white/60 border-2 border-[#FFD6D4] rounded-xl focus:border-[#DB4F40] focus:bg-white transition-all duration-300 placeholder-[#DB4F40]/60 shadow-sm hover:shadow-md"
            placeholder="Add any comments or motivation for your application (optional)"
            rows={4}
          />
        </div>

        {/* Submit Button */}
        {user ? (
          <button
            type="submit"
            disabled={loading}
            className={`w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 shadow-sm hover:shadow-md ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-[#DB4F40] hover:bg-[#c13426] text-white hover:scale-[1.02]'
            }`}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <FaSpinner className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </div>
            ) : (
              'Submit Application'
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => window.location.href = '/login'}
            className="w-full py-4 px-6 rounded-xl font-semibold transition-all duration-300 shadow-sm hover:shadow-md bg-gray-400 text-white cursor-pointer hover:bg-gray-500"
          >
            Login to apply
          </button>
        )}
      </form>

      {/* Privacy Notice */}
      <div className="mt-6 p-4 bg-[#FFEEED] rounded-xl border border-[#FFD6D4]">
        <p className="text-xs text-[#1B222A]/70">
          By submitting this application, you agree to share your information with the university for the purpose of this application. 
          Your data will be handled in accordance with our privacy policy.
        </p>
      </div>
    </div>
  );
};

export default ApplicationForm; 